import React, { useState } from "react";
import {
  SideNav,
  SideNavHeading,
  SideNavSection,
  SideNavItem,
} from "@astryxdesign/core";
import type { IconType } from "@astryxdesign/core";
import { Settings, LogOut } from "lucide-react";
import styles from "./GlobalNavRail.module.css";

const COLLAPSE_STORAGE_KEY = "shadowcue.globalNavRail.collapsed";

const readPersistedCollapsed = (): boolean =>
  window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === "true";

/** A single link-style row in the rail. */
export interface GlobalNavRailItem {
  /** Display label. */
  label: string;
  /** Route path. Selection is computed by the rail from this and `currentPath`. */
  href: string;
  /** Icon rendered before the label. */
  icon: IconType;
}

export interface GlobalNavRailProps {
  /** Primary navigation items, shown to every signed-in user. */
  items: GlobalNavRailItem[];
  /** Admin-only navigation items, appended to `items` when `isAdmin`. */
  adminItems: GlobalNavRailItem[];
  /** Whether the signed-in user is an admin — gates `adminItems` and the CTA. */
  isAdmin: boolean;
  /**
   * The current route path (e.g. `location.pathname`). Used to compute each
   * item's selected state: an exact match for `/dashboard`, `startsWith` for
   * every other item.
   */
  currentPath: string;
  /** Utility links rendered in the footer above Settings (e.g. the desktop app). */
  footerItems?: GlobalNavRailItem[];
  /** Callback fired when the Settings row is activated. */
  onOpenSettings: () => void;
  /** Callback fired when the Sign out row is activated. */
  onSignOut: () => void;
  /** Product name shown in the rail header. */
  heading?: string;
  /** Link target for the header heading. */
  headingHref: string;
  /** Icon/logo rendered beside the heading. */
  headingIcon?: React.ReactNode;
  /** Label for the Settings row. */
  settingsLabel?: string;
  /** Label for the Sign out row. */
  signOutLabel?: string;
}

const isItemSelected = (href: string, currentPath: string): boolean => {
  if (href === "/dashboard") {
    return currentPath === href;
  }
  return currentPath.startsWith(href);
};

/**
 * The application's global left navigation rail, built on Astryx's `SideNav`.
 * Renders no app/routing logic itself — the caller supplies fully-resolved
 * `items`/`adminItems` (label, href, icon) and the current path, and Astryx's
 * `LinkProvider` (mounted once at the app root) makes every rendered link
 * route through the host app's router.
 *
 * Collapse state persists to `localStorage` so it survives a reload.
 */
const GlobalNavRail = ({
  items,
  adminItems,
  isAdmin,
  currentPath,
  footerItems = [],
  onOpenSettings,
  onSignOut,
  heading = "ContxtIQ",
  headingHref,
  headingIcon,
  settingsLabel = "Settings",
  signOutLabel = "Sign out",
}: GlobalNavRailProps): React.ReactElement => {
  // Admin items simply extend the one nav list rather than sitting under their
  // own labelled section — for an admin they are just more places to go, and a
  // heading for three rows added a divider without adding meaning.
  const visibleItems = isAdmin ? [...items, ...adminItems] : items;

  const [isCollapsed, setIsCollapsed] = useState<boolean>(readPersistedCollapsed);

  const handleCollapsedChange = (next: boolean) => {
    setIsCollapsed(next);
    window.localStorage.setItem(COLLAPSE_STORAGE_KEY, String(next));
  };

  return (
    <div className={styles.rail}>
      <SideNav
        header={
          <SideNavHeading icon={headingIcon} heading={heading} headingHref={headingHref} />
        }
        collapsible={{ isCollapsed, onCollapsedChange: handleCollapsedChange }}
        footer={
          <div className={styles.navItemGroup}>
            {footerItems.map((item) => (
              <SideNavItem
                key={item.href}
                label={item.label}
                icon={item.icon}
                href={item.href}
                isSelected={isItemSelected(item.href, currentPath)}
              />
            ))}
            <SideNavItem label={settingsLabel} icon={Settings} onClick={onOpenSettings} />
            <SideNavItem label={signOutLabel} icon={LogOut} onClick={onSignOut} />
          </div>
        }
      >
        <div className={styles.navItemGroup}>
          <SideNavSection title="Primary navigation" isHeaderHidden>
            {visibleItems.map((item) => (
              <SideNavItem
                key={item.href}
                label={item.label}
                icon={item.icon}
                href={item.href}
                isSelected={isItemSelected(item.href, currentPath)}
              />
            ))}
          </SideNavSection>
        </div>
      </SideNav>
    </div>
  );
};

export default GlobalNavRail;
